import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebaseConfig';
import { collection, addDoc, serverTimestamp, query, getDocs, doc, setDoc, updateDoc, deleteDoc, orderBy, Timestamp, where, writeBatch } from 'firebase/firestore';
import { Box, Typography, Paper, TextField, Button, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Switch, FormControlLabel
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Select, MenuItem, FormControl, InputLabel, Chip } from '@mui/material';

// Collection con campo teamId che vanno pulite quando si elimina un team in modo profondo.
// Le sub-collection di workOrders/events vengono pulite separatamente perché sono nested.
const TEAM_DATA_COLLECTIONS = [
  'vehicles', 'setupSheets', 'setupTemplates', 'drivers', 'tracks',
  'inventory', 'inventoryCategories', 'suppliers', 'orders',
  'billingSummaries', 'checklistTemplates', 'componentLifing',
  'tires', 'driverFeedback',
];

// Cancella in profondità un team e tutti i dati associati.
// Restituisce un riepilogo dei documenti eliminati per collezione.
async function deepDeleteTeam(teamId) {
  const stats = {};
  // 1. Pulisci collection top-level con campo teamId
  for (const col of TEAM_DATA_COLLECTIONS) {
    try {
      const q = query(collection(db, col), where('teamId', '==', teamId));
      const snap = await getDocs(q);
      stats[col] = snap.size;
      // Firestore batch limit = 500. Spezzo se necessario.
      for (let i = 0; i < snap.docs.length; i += 400) {
        const batch = writeBatch(db);
        snap.docs.slice(i, i + 400).forEach(d => batch.delete(d.ref));
        await batch.commit();
      }
    } catch (e) {
      console.error(`Errore pulizia ${col}:`, e);
      stats[col] = `errore: ${e.message}`;
    }
  }
  // 2. Pulisci sub-collection nested per events (sessions) e workOrders (items)
  try {
    const evQ = query(collection(db, 'events'), where('teamId', '==', teamId));
    const evSnap = await getDocs(evQ);
    stats['events'] = evSnap.size;
    for (const ev of evSnap.docs) {
      const sessSnap = await getDocs(collection(db, 'events', ev.id, 'sessions'));
      for (let i = 0; i < sessSnap.docs.length; i += 400) {
        const batch = writeBatch(db);
        sessSnap.docs.slice(i, i + 400).forEach(d => batch.delete(d.ref));
        await batch.commit();
      }
      await deleteDoc(ev.ref);
    }
  } catch (e) { console.error('Errore pulizia events:', e); stats['events'] = `errore: ${e.message}`; }

  try {
    const woQ = query(collection(db, 'workOrders'), where('teamId', '==', teamId));
    const woSnap = await getDocs(woQ);
    stats['workOrders'] = woSnap.size;
    for (const wo of woSnap.docs) {
      const itemsSnap = await getDocs(collection(db, 'workOrders', wo.id, 'items'));
      for (let i = 0; i < itemsSnap.docs.length; i += 400) {
        const batch = writeBatch(db);
        itemsSnap.docs.slice(i, i + 400).forEach(d => batch.delete(d.ref));
        await batch.commit();
      }
      await deleteDoc(wo.ref);
    }
  } catch (e) { console.error('Errore pulizia workOrders:', e); stats['workOrders'] = `errore: ${e.message}`; }

  // 3. Cancella il team document stesso
  try {
    await deleteDoc(doc(db, 'teams', teamId));
    stats['teams'] = 1;
  } catch (e) { stats['teams'] = `errore: ${e.message}`; }
  return stats;
}

function SuperAdminDashboard({ user, userData }) {
  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [eulaLog, setEulaLog] = useState([]);
  const [newTeamName, setNewTeamName] = useState('');
  const [maxMembers, setMaxMembers] = useState(5);
  const [maxCars, setMaxCars] = useState(2); // Aggiunto limite auto
  const [trialDays, setTrialDays] = useState(30);
  const [isCreating, setIsCreating] = useState(false);
  const [message, setMessage] = useState('');

  // Trial duplicate check: email admin di riferimento + warning se già usata
  const [adminEmail, setAdminEmail] = useState('');
  const [trialUsage, setTrialUsage] = useState({}); // email lowercased → { firstTrialAt, teamIds }
  const adminEmailLower = adminEmail.trim().toLowerCase();
  const existingTrialForEmail = adminEmailLower ? trialUsage[adminEmailLower] : null;
  
  // Sicurezza Extra Locale
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState(false);

  // Modifica Team Esistente
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [editMaxMembers, setEditMaxMembers] = useState('');
  const [editMaxCars, setEditMaxCars] = useState('');
  const [editTrialDate, setEditTrialDate] = useState('');
  const [editPlan, setEditPlan] = useState('trial'); // 'trial' | 'active'

  // Cancellazione Team
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingTeam, setDeletingTeam] = useState(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deepDeleteMode, setDeepDeleteMode] = useState(true); // default cancellazione profonda
  const [lastDeleteStats, setLastDeleteStats] = useState(null);

  // Filtro UI: mostra solo le trial scadute >30gg
  const [showExpiredOnly, setShowExpiredOnly] = useState(false);
  const [bulkExpiredDialogOpen, setBulkExpiredDialogOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [bulkDeleteStats, setBulkDeleteStats] = useState(null);

  // Carica tutti i team esistenti e il log EULA
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const q = query(collection(db, 'teams'));
        const querySnapshot = await getDocs(q);
        const teamsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setTeams(teamsList);
      } catch (error) {
        console.error("Errore caricamento team:", error);
      } finally {
        setIsLoading(false);
      }
    };
    const fetchEulaLog = async () => {
      try {
        const snap = await getDocs(collection(db, 'eulaLog'));
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (b.acceptedAt?.seconds || 0) - (a.acceptedAt?.seconds || 0));
        setEulaLog(list);
      } catch (e) {
        console.error("Errore caricamento eulaLog:", e);
      }
    };
    const fetchTrialUsage = async () => {
      try {
        const snap = await getDocs(collection(db, 'trialUsage'));
        const map = {};
        snap.docs.forEach(d => { map[d.id] = d.data(); });
        setTrialUsage(map);
      } catch (e) {
        console.error("Errore caricamento trialUsage:", e);
      }
    };
    fetchTeams();
    fetchEulaLog();
    fetchTrialUsage();
  }, []);

  const generateTeamCode = () => {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  const handleGenerateMissingCode = async (teamId) => {
    try {
      const teamRef = doc(db, 'teams', teamId);
      const newCode = generateTeamCode();
      await updateDoc(teamRef, { teamCode: newCode });
      
      setTeams(teams.map(t => {
        if (t.id === teamId) {
          return { ...t, teamCode: newCode };
        }
        return t;
      }));
    } catch (err) {
      console.error("Errore generazione codice:", err);
      alert("Errore durante la generazione del codice");
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!newTeamName.trim()) return;

    setIsCreating(true);
    setMessage('');

    try {
      const teamCode = generateTeamCode();
      
      const trialEnds = new Date();
      trialEnds.setDate(trialEnds.getDate() + Number(trialDays));
      
      const teamData = {
        nomeTeam: newTeamName.trim(),
        teamCode: teamCode,
        maxMembers: Number(maxMembers),
        maxCars: Number(maxCars) || 0, // 0 = illimitate, ma di default 2
        createdAt: serverTimestamp(),
        trialEndsAt: Timestamp.fromDate(trialEnds),
        membri: {},
        subscriptionPlan: 'Beta',
        costoOrario: 0,
        pitLaneBuffer: 0,
        adminEmail: adminEmailLower || null,
        // Quota AI iniziale (trial)
        aiAnalysesUsed: 0,
        aiAnalysesLimit: 20, // override del default, sovrascritto quando converte a piano pagante
      };

      const teamRef = await addDoc(collection(db, 'teams'), teamData);

      // Registra l'uso del trial per questa email (per prevenire trial multiple)
      if (adminEmailLower) {
        const usageRef = doc(db, 'trialUsage', adminEmailLower);
        try {
          const existing = trialUsage[adminEmailLower];
          await setDoc(usageRef, {
            email: adminEmailLower,
            firstTrialAt: existing?.firstTrialAt || serverTimestamp(),
            lastTrialAt: serverTimestamp(),
            teamIds: existing?.teamIds ? [...existing.teamIds, teamRef.id] : [teamRef.id],
            trialCount: (existing?.trialCount || 0) + 1,
          }, { merge: true });
          setTrialUsage(prev => ({
            ...prev,
            [adminEmailLower]: {
              email: adminEmailLower,
              firstTrialAt: existing?.firstTrialAt || { toDate: () => new Date() },
              lastTrialAt: { toDate: () => new Date() },
              teamIds: existing?.teamIds ? [...existing.teamIds, teamRef.id] : [teamRef.id],
              trialCount: (existing?.trialCount || 0) + 1,
            }
          }));
        } catch (e) { console.warn("Errore registrazione trialUsage:", e); }
      }

      // Rilegge tutti i team da Firestore per evitare disallineamenti di tipo
      const snap = await getDocs(query(collection(db, 'teams')));
      setTeams(snap.docs.map(d => ({ id: d.id, ...d.data() })));

      setNewTeamName('');
      setMaxMembers(5);
      setMaxCars(2);
      setAdminEmail('');
      setMessage(`Team creato con successo! Codice d'accesso: ${teamCode}`);
    } catch (error) {
      console.error("Errore creazione team:", error);
      setMessage("Errore durante la creazione del team.");
    } finally {
      setIsCreating(false);
    }
  };

  const handleUnlock = (e) => {
    e.preventDefault();
    
    // Rimuove eventuali spazi vuoti iniziali/finali o virgolette inserite per sbaglio su Firebase
    const correctPassword = (userData?.superAdminPassword || 'motorsport_admin').trim().replace(/['"]/g, '');
    const enteredPassword = passwordInput.trim().replace(/['"]/g, '');

    if (enteredPassword === correctPassword) {
      setIsUnlocked(true);
      setPasswordError(false);
    } else {
      setPasswordError(true);
    }
  };

  // --- LOGICA MODIFICA TEAM ---
  const handleOpenEdit = (team) => {
    setEditingTeam(team);
    setEditMaxMembers(team.maxMembers || 5);
    setEditMaxCars(team.maxCars || 0);
    if (team.trialEndsAt) {
      setEditTrialDate(team.trialEndsAt.toDate().toISOString().split('T')[0]);
      setEditPlan('trial');
    } else {
      setEditTrialDate('');
      setEditPlan('active');
    }
    setEditDialogOpen(true);
  };

  const handleCloseEdit = () => {
    setEditDialogOpen(false);
    setEditingTeam(null);
  };

  const handleSaveEdit = async () => {
    if (!editingTeam) return;
    try {
      const teamRef = doc(db, 'teams', editingTeam.id);
      
      let newTrialEndsAt = null;
      if (editTrialDate) {
        newTrialEndsAt = new Date(editTrialDate);
      }
      
      const finalTrialEndsAt = editPlan === 'active' ? null : newTrialEndsAt;

      await updateDoc(teamRef, {
        maxMembers: Number(editMaxMembers),
        maxCars: Number(editMaxCars) || 0,
        trialEndsAt: finalTrialEndsAt,
        subscriptionPlan: editPlan === 'active' ? 'Attivo' : 'Trial',
      });

      setTeams(teams.map(t => {
        if (t.id === editingTeam.id) {
          return {
            ...t,
            maxMembers: Number(editMaxMembers),
            maxCars: Number(editMaxCars) || 0,
            trialEndsAt: finalTrialEndsAt ? { toDate: () => finalTrialEndsAt } : null,
            subscriptionPlan: editPlan === 'active' ? 'Attivo' : 'Trial',
          };
        }
        return t;
      }));
      
      handleCloseEdit();
    } catch (err) {
      console.error("Errore aggiornamento:", err);
      alert("Errore durante l'aggiornamento del team");
    }
  };
  // --- FINE LOGICA MODIFICA TEAM ---

  // --- LOGICA CANCELLAZIONE TEAM ---
  const handleOpenDelete = (team) => {
    setDeletingTeam(team);
    setDeleteConfirmName('');
    setDeleteDialogOpen(true);
  };

  const handleCloseDelete = () => {
    setDeleteDialogOpen(false);
    setDeletingTeam(null);
    setDeleteConfirmName('');
  };

  const handleConfirmDelete = async () => {
    if (!deletingTeam || deleteConfirmName !== deletingTeam.nomeTeam) return;
    setIsDeleting(true);
    setLastDeleteStats(null);
    try {
      let stats = null;
      if (deepDeleteMode) {
        stats = await deepDeleteTeam(deletingTeam.id);
        setLastDeleteStats(stats);
      } else {
        await deleteDoc(doc(db, 'teams', deletingTeam.id));
      }
      setTeams(teams.filter(t => t.id !== deletingTeam.id));
      const summary = stats
        ? ` (${Object.entries(stats).filter(([_, v]) => typeof v === 'number').reduce((s, [_, v]) => s + v, 0)} documenti rimossi)`
        : '';
      setMessage(`Team "${deletingTeam.nomeTeam}" eliminato${summary}.`);
      handleCloseDelete();
    } catch (err) {
      console.error('Errore cancellazione team:', err);
      alert('Errore durante la cancellazione: ' + err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  // --- BULK CLEANUP TRIAL SCADUTE ---
  // Trial considerata "scaduta da pulire" se: trialEndsAt < oggi - 30gg E subscriptionPlan in [Beta, trial]
  const expiredTrialTeams = useMemo(() => {
    const now = Date.now();
    const cutoff = now - 30 * 24 * 60 * 60 * 1000; // 30 giorni fa
    return teams.filter(t => {
      const ends = t.trialEndsAt?.toDate?.()?.getTime?.();
      if (!ends) return false;
      const isTrialPlan = !t.subscriptionPlan || ['Beta', 'trial', 'Trial'].includes(t.subscriptionPlan);
      return ends < cutoff && isTrialPlan;
    });
  }, [teams]);

  const handleBulkDeleteExpired = async () => {
    setIsBulkDeleting(true);
    const allStats = { teams: 0 };
    const survivors = [];
    for (const team of expiredTrialTeams) {
      try {
        const stats = await deepDeleteTeam(team.id);
        Object.entries(stats).forEach(([col, n]) => {
          if (typeof n === 'number') allStats[col] = (allStats[col] || 0) + n;
        });
      } catch (e) {
        console.error(`Errore deepDelete su ${team.nomeTeam}:`, e);
        survivors.push(team);
      }
    }
    // Rimuovi i team eliminati dalla lista locale (mantieni quelli falliti)
    const failedIds = new Set(survivors.map(t => t.id));
    setTeams(teams.filter(t => !expiredTrialTeams.includes(t) || failedIds.has(t.id)));
    setBulkDeleteStats(allStats);
    setMessage(`Eliminati ${expiredTrialTeams.length - survivors.length}/${expiredTrialTeams.length} team scaduti.`);
    setIsBulkDeleting(false);
    setBulkExpiredDialogOpen(false);
  };
  // --- FINE LOGICA CANCELLAZIONE TEAM ---

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;
  }

  // --- SCHERMATA BLOCCO PASSWORD ---
  if (!isUnlocked) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', p: 3 }}>
        <Paper sx={{ p: 4, textAlign: 'center', width: '100%', maxWidth: '400px' }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>Area Riservata</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Inserisci la password di sicurezza per accedere alla Dashboard Super Admin.
          </Typography>
          <Box component="form" onSubmit={handleUnlock} sx={{ width: '100%' }}>
            <TextField
              fullWidth
              type="password"
              label="Password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              error={passwordError}
              helperText={passwordError ? "Password errata" : ""}
              sx={{ mb: 3 }}
              autoFocus
            />
            <Button type="submit" fullWidth variant="contained" size="large">
              Sblocca
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <Typography variant="h4" gutterBottom>👑 Super Admin Dashboard</Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Da qui puoi creare nuovi Team e assegnare i codici d'accesso ai Team Principal.
      </Typography>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>Crea un Nuovo Team</Typography>
        <Box component="form" onSubmit={handleCreateTeam} sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Nome Team"
            value={newTeamName}
            onChange={(e) => setNewTeamName(e.target.value)}
            required
            sx={{ flexGrow: 1, minWidth: 200 }}
          />
          <TextField
            label="Email admin (anti-trial duplicate)"
            type="email"
            value={adminEmail}
            onChange={(e) => setAdminEmail(e.target.value)}
            sx={{ flexGrow: 1, minWidth: 240 }}
            helperText="Email usata come riferimento per evitare trial multiple"
          />
          <TextField
            label="Limite Membri"
            type="number"
            value={maxMembers}
            onChange={(e) => setMaxMembers(e.target.value)}
            required
            sx={{ width: 150 }}
            inputProps={{ min: 1 }}
          />
          <TextField
            label="Limite Auto"
            type="number"
            value={maxCars}
            onChange={(e) => setMaxCars(e.target.value)}
            required
            sx={{ width: 150 }}
            inputProps={{ min: 0 }}
          />
          <TextField
            label="Giorni di Prova"
            type="number"
            value={trialDays}
            onChange={(e) => setTrialDays(e.target.value)}
            required
            sx={{ width: 150 }}
            inputProps={{ min: 1 }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={isCreating}
            sx={{ height: 56 }}
          >
            {isCreating ? <CircularProgress size={24} /> : "Crea Team"}
          </Button>
        </Box>
        {existingTrialForEmail && (
          <Alert severity="warning" sx={{ mt: 2 }} icon={<WarningAmberIcon />}>
            <strong>Questa email ha già attivato {existingTrialForEmail.trialCount || 1} trial</strong>
            {existingTrialForEmail.firstTrialAt?.toDate?.()
              ? ` (prima volta il ${existingTrialForEmail.firstTrialAt.toDate().toLocaleDateString('it-IT')})`
              : ''}
            . Team già creati: {(existingTrialForEmail.teamIds || []).length}.
            Crea comunque se è un caso legittimo (es. preparatore con più scuderie reali).
          </Alert>
        )}
        {message && <Alert severity={message.includes('Errore') ? 'error' : 'success'} sx={{ mt: 2 }}>{message}</Alert>}
      </Paper>

      {/* Banner trial scadute */}
      {expiredTrialTeams.length > 0 && (
        <Alert
          severity="warning"
          icon={<WarningAmberIcon />}
          sx={{ mb: 2 }}
          action={
            <Button
              color="error"
              variant="contained"
              size="small"
              startIcon={<DeleteForeverIcon />}
              onClick={() => setBulkExpiredDialogOpen(true)}
            >
              Elimina tutte ({expiredTrialTeams.length})
            </Button>
          }
        >
          <strong>{expiredTrialTeams.length} trial scadute da oltre 30 giorni</strong> e mai convertite a piano pagante.
          Puoi eliminarle in blocco insieme a tutti i loro dati (vehicles, setups, drivers, ecc.).
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6">
          {showExpiredOnly ? `Trial scadute (${expiredTrialTeams.length})` : `Team Attivi nel Database (${teams.length})`}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={showExpiredOnly}
              onChange={(e) => setShowExpiredOnly(e.target.checked)}
              color="warning"
            />
          }
          label="Mostra solo trial scadute >30gg"
        />
      </Box>

      {bulkDeleteStats && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setBulkDeleteStats(null)}>
          <strong>Bulk cleanup completato.</strong> Documenti rimossi:&nbsp;
          {Object.entries(bulkDeleteStats).filter(([_, v]) => typeof v === 'number' && v > 0).map(([k, v]) => `${k}: ${v}`).join(' · ')}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome Team</TableCell>
              <TableCell>Codice Accesso</TableCell>
              <TableCell>Stato</TableCell>
              <TableCell>Limite Membri</TableCell>
              <TableCell>Limite Auto</TableCell>
              <TableCell>Membri</TableCell>
              <TableCell>Scadenza</TableCell>
              <TableCell align="center">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(showExpiredOnly ? expiredTrialTeams : teams).map((team) => (
              <TableRow key={team.id}>
                <TableCell>{team.nomeTeam}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {team.teamCode ? (
                    team.teamCode
                  ) : (
                    <Button size="small" variant="outlined" onClick={() => handleGenerateMissingCode(team.id)}>
                      Genera Codice
                    </Button>
                  )}
                </TableCell>
                <TableCell>
                  {!team.trialEndsAt ? (
                    <Chip label="Attivo" color="success" size="small" />
                  ) : team.trialEndsAt.toDate() < new Date() ? (
                    <Chip label="Scaduto" color="error" size="small" />
                  ) : (
                    <Chip label="Trial" color="warning" size="small" />
                  )}
                </TableCell>
                <TableCell>{team.maxMembers || 'Illimitati'}</TableCell>
                <TableCell>{team.maxCars > 0 ? team.maxCars : 'Illimitate'}</TableCell>
                <TableCell>{Object.keys(team.membri || {}).length}</TableCell>
                <TableCell>
                  {team.trialEndsAt ? team.trialEndsAt.toDate().toLocaleDateString('it-IT') : '—'}
                </TableCell>
                <TableCell align="center">
                  <IconButton onClick={() => handleOpenEdit(team)} color="primary">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleOpenDelete(team)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* SEZIONE EULA LOG */}
      <Typography variant="h6" gutterBottom sx={{ mt: 5 }}>Accettazioni EULA</Typography>
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Nome dichiarato</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Team</strong></TableCell>
              <TableCell><strong>Data/Ora</strong></TableCell>
              <TableCell><strong>Versione</strong></TableCell>
              <TableCell><strong>Dispositivo</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {eulaLog.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>Nessuna accettazione ancora registrata.</TableCell></TableRow>
            ) : (() => {
              // Mappa userId -> team corrente (cercando nei membri di ogni team)
              const userToTeam = {};
              teams.forEach(t => {
                Object.keys(t.membri || {}).forEach(uid => { userToTeam[uid] = t; });
              });
              // Mappa teamId -> team (per fallback su teamId salvato nell'eulaLog)
              const teamById = {};
              teams.forEach(t => { teamById[t.id] = t; });

              return eulaLog.map((entry) => {
                const currentTeam = userToTeam[entry.userId] || teamById[entry.teamId];
                const teamLabel = currentTeam
                  ? `${currentTeam.nomeTeam}${currentTeam.teamCode ? ' · ' + currentTeam.teamCode : ''}`
                  : (entry.teamId ? 'Team eliminato' : '—');
                return (
                  <TableRow key={entry.id} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell sx={{ fontWeight: 600 }}>{entry.name || '—'}</TableCell>
                    <TableCell>{entry.email || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12, fontWeight: currentTeam ? 600 : 400, color: currentTeam ? 'text.primary' : 'text.disabled' }}>
                      {teamLabel}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {entry.acceptedAt?.toDate
                        ? entry.acceptedAt.toDate().toLocaleString('it-IT')
                        : '—'}
                    </TableCell>
                    <TableCell>{entry.acceptedVersion || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 10, color: 'text.secondary', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {entry.ua || '—'}
                    </TableCell>
                  </TableRow>
                );
              });
            })()}
          </TableBody>
        </Table>
      </TableContainer>

      {/* MODALE CANCELLAZIONE SINGOLA */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDelete} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main', fontWeight: 700 }}>
          Elimina Team
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Stai per eliminare il team <strong>{deletingTeam?.nomeTeam}</strong>.
            I membri perderanno l'accesso immediatamente.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={deepDeleteMode}
                onChange={(e) => setDeepDeleteMode(e.target.checked)}
                color="error"
              />
            }
            label={
              <Typography variant="body2">
                <strong>Cancellazione profonda</strong> — elimina anche vehicles, setups, drivers, gomme, officina, fatture, eventi.
              </Typography>
            }
            sx={{ mb: 2, alignItems: 'flex-start' }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Scrivi il nome del team per confermare:
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder={deletingTeam?.nomeTeam}
            value={deleteConfirmName}
            onChange={(e) => setDeleteConfirmName(e.target.value)}
            autoFocus
          />
          {lastDeleteStats && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Ultima eliminazione: {Object.entries(lastDeleteStats).filter(([_, v]) => typeof v === 'number' && v > 0).map(([k, v]) => `${k}: ${v}`).join(' · ')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete} disabled={isDeleting}>Annulla</Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleteConfirmName !== deletingTeam?.nomeTeam || isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : (deepDeleteMode ? <DeleteForeverIcon /> : <DeleteIcon />)}
          >
            {isDeleting ? 'Eliminando…' : (deepDeleteMode ? 'Elimina con dati' : 'Elimina')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODALE BULK CLEANUP TRIAL SCADUTE */}
      <Dialog open={bulkExpiredDialogOpen} onClose={() => !isBulkDeleting && setBulkExpiredDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteForeverIcon /> Cleanup trial scadute
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Stai per <strong>eliminare definitivamente {expiredTrialTeams.length} team</strong> e tutti i loro dati associati.
            L'operazione non è reversibile.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>Team da eliminare:</Typography>
          <Box sx={{ maxHeight: 240, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
            {expiredTrialTeams.map(t => (
              <Box key={t.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}>
                <Typography variant="body2">{t.nomeTeam}</Typography>
                <Typography variant="caption" color="text.secondary">
                  scaduto il {t.trialEndsAt?.toDate?.()?.toLocaleDateString('it-IT')}
                </Typography>
              </Box>
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Vengono pulite anche tutte le collezioni associate: vehicles, setupSheets, setupTemplates, drivers, tracks, inventory, suppliers, orders, workOrders+items, events+sessions, tires, fatture, ecc.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkExpiredDialogOpen(false)} disabled={isBulkDeleting}>Annulla</Button>
          <Button
            onClick={handleBulkDeleteExpired}
            variant="contained"
            color="error"
            disabled={isBulkDeleting || expiredTrialTeams.length === 0}
            startIcon={isBulkDeleting ? <CircularProgress size={16} color="inherit" /> : <DeleteForeverIcon />}
          >
            {isBulkDeleting ? 'Eliminando…' : `Elimina ${expiredTrialTeams.length} team`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODALE DI MODIFICA */}
      <Dialog open={editDialogOpen} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Modifica Team: {editingTeam?.nomeTeam}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          <TextField
            label="Limite Membri"
            type="number"
            fullWidth
            value={editMaxMembers}
            onChange={(e) => setEditMaxMembers(e.target.value)}
            inputProps={{ min: 1 }}
            sx={{ mt: 1 }}
          />
          <TextField
            label="Limite Auto (0 = illimitate)"
            type="number"
            fullWidth
            value={editMaxCars}
            onChange={(e) => setEditMaxCars(e.target.value)}
            inputProps={{ min: 0 }}
            sx={{ mt: 1 }}
          />
          <FormControl fullWidth>
            <InputLabel>Piano</InputLabel>
            <Select
              value={editPlan}
              label="Piano"
              onChange={(e) => setEditPlan(e.target.value)}
            >
              <MenuItem value="trial">Trial (con scadenza)</MenuItem>
              <MenuItem value="active">Attivo — Nessuna scadenza</MenuItem>
            </Select>
          </FormControl>
          {editPlan === 'trial' && (
            <TextField
              label="Data di Scadenza"
              type="date"
              fullWidth
              value={editTrialDate}
              onChange={(e) => setEditTrialDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Annulla</Button>
          <Button onClick={handleSaveEdit} variant="contained">Salva Modifiche</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default SuperAdminDashboard;
